import React, { useState, useEffect } from 'react';
import { useAdmin } from '../AdminApp';
import { toast } from 'sonner';
import {
  Search, Mail, Clock, Download, Send, Filter, RefreshCw,
  ChevronLeft, ChevronRight, CheckCircle, Users, TrendingUp,
  BarChart3, Share2, Award, Globe, Inbox, Eye,
  UserCheck as UserCheckIcon,
} from 'lucide-react';

const STATUS_OPTIONS = [
  { value: 'waiting', label: 'Waiting', color: 'bg-amber-100 text-amber-700' },
  { value: 'invited', label: 'Invited', color: 'bg-blue-100 text-blue-700' },
  { value: 'downloaded', label: 'Downloaded', color: 'bg-emerald-100 text-emerald-700' },
  // Round 6: waitlist + app signups are ONE funnel list — "registered"
  // rows actually created an account (web or mobile).
  { value: 'registered', label: 'Registered', color: 'bg-purple-100 text-purple-700' },
];

const WaitlistPage = () => {
  const { api } = useAdmin();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [stats, setStats] = useState({ waiting: 0, invited: 0, downloaded: 0 });
  const [analytics, setAnalytics] = useState(null);
  const [activeTab, setActiveTab] = useState('entries');
  const [sortBy, setSortBy] = useState('signed_up_at');
  const [emailLogs, setEmailLogs] = useState([]);
  const [emailStats, setEmailStats] = useState({});
  const [emailPage, setEmailPage] = useState(1);
  const [emailTotalPages, setEmailTotalPages] = useState(1);
  const [emailTypeFilter, setEmailTypeFilter] = useState('');
  const [emailSearch, setEmailSearch] = useState('');
  const [previewHtml, setPreviewHtml] = useState(null);
  const limit = 25;

  const fetchWaitlist = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        sort_by: sortBy,
      });
      if (search) params.append('search', search);
      if (statusFilter) params.append('status', statusFilter);

      const response = await api.get(`/api/admin/waitlist?${params}`);
      setEntries(response.data.entries || []);
      setTotalPages(response.data.total_pages || 1);
      setTotalCount(response.data.total_count || 0);
      setStats(response.data.stats || { waiting: 0, invited: 0, downloaded: 0 });
    } catch (error) {
      toast.error('Failed to load waitlist');
    } finally {
      setLoading(false);
    }
  };

  const fetchAnalytics = async () => {
    try {
      const response = await api.get('/api/admin/waitlist/analytics');
      setAnalytics(response.data);
    } catch (error) {
      console.error('Analytics fetch error:', error);
    }
  };

  const fetchEmailLogs = async () => {
    try {
      const params = new URLSearchParams({
        page: emailPage.toString(),
        limit: '25',
      });
      if (emailTypeFilter) params.append('email_type', emailTypeFilter);
      if (emailSearch) params.append('search', emailSearch);
      const response = await api.get(`/api/admin/email-logs?${params}`);
      setEmailLogs(response.data.entries || []);
      setEmailStats(response.data.stats || {});
      setEmailTotalPages(response.data.total_pages || 1);
    } catch (error) {
      console.error('Email logs fetch error:', error);
    }
  };

  const handlePreviewEmail = async (logId) => {
    try {
      const response = await api.get(`/api/admin/email-logs/${logId}/preview`);
      setPreviewHtml(response.data.html_body);
    } catch (error) {
      toast.error('Failed to load email preview');
    }
  };

  useEffect(() => {
    fetchWaitlist();
    fetchAnalytics();
    fetchEmailLogs();
  }, [page, statusFilter, sortBy]);

  useEffect(() => {
    fetchEmailLogs();
  }, [emailPage, emailTypeFilter]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchWaitlist();
  };

  const updateStatus = async (entryId, newStatus) => {
    try {
      await api.patch(`/api/admin/waitlist/${entryId}/status`, { status: newStatus });
      toast.success(`Status updated to ${newStatus}`);
      fetchWaitlist();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const exportCsv = () => {
    const csv = [
      'Email,Username,Status,Signed Up,Source,Referral Count,Referred By,UTM Source',
      ...entries.map(e =>
        `${e.email},${e.username || ''},${e.status},${new Date(e.signed_up_at).toLocaleDateString()},${e.source || 'N/A'},${e.referral_count || 0},${e.referred_by_user_id || ''},${e.utm_source || ''}`
      ),
    ].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `waitlist_export_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Waitlist exported');
  };

  const getStatusBadge = (status) => {
    const opt = STATUS_OPTIONS.find(s => s.value === status) || STATUS_OPTIONS[0];
    return (
      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${opt.color}`}>
        {opt.label}
      </span>
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const tabs = [
    { id: 'entries', label: 'Waitlist Entries', icon: Users },
    { id: 'analytics', label: 'Analytics', icon: BarChart3 },
    { id: 'referrers', label: 'Top Referrers', icon: Award },
    { id: 'emails', label: 'Email Logs', icon: Mail },
  ];

  return (
    <div className="space-y-6" data-testid="waitlist-admin-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Waitlist Management</h1>
          <p className="text-sm text-gray-500 mt-1">{totalCount} total signups</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => { fetchWaitlist(); fetchAnalytics(); }}
            className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            data-testid="waitlist-refresh"
          >
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
          <button
            onClick={exportCsv}
            className="flex items-center gap-2 px-3 py-2 text-sm bg-emerald-50 text-emerald-700 hover:bg-emerald-100 rounded-lg transition-colors"
            data-testid="waitlist-export"
          >
            <Download className="w-4 h-4" /> Export CSV
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
              <Users className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{totalCount}</p>
              <p className="text-xs text-gray-500">Total Signups</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{analytics?.today_signups || 0}</p>
              <p className="text-xs text-gray-500">Today</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-violet-100 rounded-lg flex items-center justify-center">
              <Share2 className="w-5 h-5 text-violet-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{analytics?.total_referred || 0}</p>
              <p className="text-xs text-gray-500">Via Referral</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
              <Clock className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{stats.waiting}</p>
              <p className="text-xs text-gray-500">Waiting</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
              <UserCheckIcon className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{stats.registered || 0}</p>
              <p className="text-xs text-gray-500">Registered (using the app)</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <div className="flex gap-1 overflow-x-auto">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                activeTab === tab.id
                  ? 'border-emerald-500 text-emerald-700'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Entries Tab */}
      {activeTab === 'entries' && (
        <>
          {/* Search, Filter, Sort */}
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex flex-col sm:flex-row gap-3">
              <form onSubmit={handleSearch} className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by email or username..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                  data-testid="waitlist-search-input"
                />
              </form>
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-gray-400" />
                <select
                  value={statusFilter}
                  onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
                  className="border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
                  data-testid="waitlist-status-filter"
                >
                  <option value="">All Status</option>
                  {STATUS_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                <select
                  value={sortBy}
                  onChange={(e) => { setSortBy(e.target.value); setPage(1); }}
                  className="border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
                >
                  <option value="signed_up_at">Newest First</option>
                  <option value="referrals">Most Referrals</option>
                </select>
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <div className="animate-spin w-6 h-6 border-2 border-emerald-500 border-t-transparent rounded-full" />
              </div>
            ) : entries.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20 text-gray-400">
                <Users className="w-12 h-12 mb-3" />
                <p className="text-lg font-medium">No waitlist entries</p>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full" data-testid="waitlist-table">
                    <thead>
                      <tr className="border-b border-gray-100 bg-gray-50/50">
                        <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Email</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Status</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Signed Up</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Source</th>
                        <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Referrals</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Referred By</th>
                        <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {entries.map((entry) => (
                        <tr key={entry.id} className="hover:bg-gray-50/50 transition-colors" data-testid={`waitlist-entry-${entry.id}`}>
                          <td className="px-4 py-3">
                            <div>
                              <span className="text-sm font-medium text-gray-900">{entry.email}</span>
                              {entry.username && (
                                <p className="text-xs text-gray-500">@{entry.username}</p>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3">{getStatusBadge(entry.status)}</td>
                          <td className="px-4 py-3 text-sm text-gray-500">{formatDate(entry.signed_up_at)}</td>
                          <td className="px-4 py-3">
                            <span className="text-sm text-gray-500 capitalize">{entry.source || 'direct'}</span>
                            {entry.utm_source && (
                              <span className="ml-1 text-xs text-violet-600 bg-violet-50 px-1.5 py-0.5 rounded">{entry.utm_source}</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className={`text-sm font-medium ${(entry.referral_count || 0) > 0 ? 'text-emerald-600' : 'text-gray-400'}`}>
                              {entry.referral_count || 0}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-400 font-mono truncate max-w-[120px]">
                            {entry.referred_by_user_id ? entry.referred_by_user_id.slice(0, 8) + '...' : '-'}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <div className="flex items-center justify-end gap-1">
                              {STATUS_OPTIONS.filter(s => s.value !== entry.status).map(opt => (
                                <button
                                  key={opt.value}
                                  onClick={() => updateStatus(entry.id, opt.value)}
                                  className={`text-xs px-2 py-1 rounded ${opt.color} hover:opacity-80 transition-opacity`}
                                  title={`Mark as ${opt.label}`}
                                >
                                  {opt.label}
                                </button>
                              ))}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100">
                    <p className="text-sm text-gray-500">Page {page} of {totalPages}</p>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setPage(p => Math.max(1, p - 1))}
                        disabled={page <= 1}
                        className="p-2 text-gray-400 hover:text-gray-600 disabled:opacity-30 rounded-lg hover:bg-gray-100"
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                        disabled={page >= totalPages}
                        className="p-2 text-gray-400 hover:text-gray-600 disabled:opacity-30 rounded-lg hover:bg-gray-100"
                      >
                        <ChevronRight className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </>
      )}

      {/* Analytics Tab */}
      {activeTab === 'analytics' && analytics && (
        <div className="space-y-6">
          {/* Source Breakdown */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Globe className="w-5 h-5 text-gray-400" /> Signup Source Breakdown
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {Object.entries(analytics.source_breakdown || {}).map(([source, count]) => (
                <div key={source} className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500 capitalize mb-1">{source.replace(/_/g, ' ')}</p>
                  <p className="text-xl font-bold text-gray-900">{count}</p>
                  <div className="w-full bg-gray-200 rounded-full h-1.5 mt-2">
                    <div
                      className="bg-emerald-500 rounded-full h-1.5"
                      style={{ width: `${Math.min(100, (count / totalCount) * 100)}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* UTM Breakdown */}
          {Object.keys(analytics.utm_breakdown || {}).length > 0 && (
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="font-semibold text-gray-900 mb-4">UTM Source Tracking</h3>
              <div className="space-y-2">
                {Object.entries(analytics.utm_breakdown).map(([utm, count]) => (
                  <div key={utm} className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0">
                    <span className="text-sm text-gray-700 capitalize">{utm}</span>
                    <span className="font-medium text-gray-900">{count}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Daily Signups */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-gray-400" /> Daily Signups (Last 30 Days)
            </h3>
            {(analytics.daily_signups || []).length > 0 ? (
              <div className="space-y-1">
                {analytics.daily_signups.slice(0, 14).map((day) => (
                  <div key={day.date} className="flex items-center gap-3">
                    <span className="text-xs text-gray-500 w-24 shrink-0">{day.date}</span>
                    <div className="flex-1 bg-gray-100 rounded-full h-5 overflow-hidden">
                      <div
                        className="bg-emerald-500 h-5 rounded-full flex items-center px-2"
                        style={{ width: `${Math.max(5, Math.min(100, (day.count / Math.max(...analytics.daily_signups.map(d => d.count))) * 100))}%` }}
                      >
                        <span className="text-xs font-medium text-white">{day.count}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-400 text-sm">No signup data yet</p>
            )}
          </div>

          {/* Summary Stats */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h3 className="font-semibold text-gray-900 mb-4">Referral Summary</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-500">Total Referred Signups</p>
                <p className="text-2xl font-bold text-gray-900">{analytics.total_referred || 0}</p>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-500">Users Who Referred Others</p>
                <p className="text-2xl font-bold text-gray-900">{analytics.total_with_referrals || 0}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Top Referrers Tab */}
      {activeTab === 'referrers' && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="p-4 border-b border-gray-100">
            <h3 className="font-semibold text-gray-900">Top Referrers Leaderboard</h3>
            <p className="text-sm text-gray-500 mt-1">Users who have referred the most signups</p>
          </div>
          <table className="w-full" data-testid="waitlist-referrers-table">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50/50">
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase w-12">Rank</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Email</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Username</th>
                <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Referrals</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Joined</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {(analytics?.top_referrers || []).map((ref, idx) => (
                <tr key={ref.id} className="hover:bg-gray-50/50">
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold ${
                      idx === 0 ? 'bg-amber-100 text-amber-700' :
                      idx === 1 ? 'bg-gray-100 text-gray-600' :
                      idx === 2 ? 'bg-orange-100 text-orange-700' :
                      'bg-gray-50 text-gray-500'
                    }`}>
                      {idx + 1}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">{ref.email}</td>
                  <td className="px-4 py-3 text-sm text-gray-500">{ref.username || '-'}</td>
                  <td className="px-4 py-3 text-center">
                    <span className="text-sm font-bold text-emerald-600">{ref.referral_count}</span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500">{formatDate(ref.signed_up_at)}</td>
                </tr>
              ))}
              {(!analytics?.top_referrers || analytics.top_referrers.length === 0) && (
                <tr>
                  <td colSpan={5} className="px-4 py-12 text-center text-gray-400">
                    <Award className="w-10 h-10 mx-auto mb-3 opacity-40" />
                    <p>No referrals yet. Share waitlist links to start tracking.</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Email Logs Tab */}
      {activeTab === 'emails' && (
        <div className="space-y-4" data-testid="email-logs-tab">
          {/* Email Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            <div className="bg-white rounded-xl border border-gray-200 p-4 text-center">
              <p className="text-xs text-gray-500">Signup Emails</p>
              <p className="text-2xl font-bold text-gray-900" data-testid="email-stat-signup">{emailStats.email_sent_signup || 0}</p>
            </div>
            <div className="bg-white rounded-xl border border-gray-200 p-4 text-center">
              <p className="text-xs text-gray-500">Referral Updates</p>
              <p className="text-2xl font-bold text-gray-900" data-testid="email-stat-referral">{emailStats.email_sent_referral_update || 0}</p>
            </div>
            <div className="bg-white rounded-xl border border-gray-200 p-4 text-center">
              <p className="text-xs text-gray-500">Beta Invites</p>
              <p className="text-2xl font-bold text-gray-900" data-testid="email-stat-beta">{emailStats.email_sent_beta_invite || 0}</p>
            </div>
            <div className="bg-white rounded-xl border border-gray-200 p-4 text-center">
              <p className="text-xs text-gray-500">Sent OK</p>
              <p className="text-2xl font-bold text-emerald-600">{emailStats.total_sent || 0}</p>
            </div>
            <div className="bg-white rounded-xl border border-gray-200 p-4 text-center">
              <p className="text-xs text-gray-500">Failed</p>
              <p className="text-2xl font-bold text-red-600">{emailStats.total_failed || 0}</p>
            </div>
          </div>

          {/* Filters */}
          <div className="bg-white rounded-xl border border-gray-200 p-4 flex flex-col sm:flex-row gap-3">
            <form onSubmit={(e) => { e.preventDefault(); fetchEmailLogs(); }} className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search by email..."
                value={emailSearch}
                onChange={(e) => setEmailSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                data-testid="email-logs-search"
              />
            </form>
            <select
              value={emailTypeFilter}
              onChange={(e) => { setEmailTypeFilter(e.target.value); setEmailPage(1); }}
              className="border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
              data-testid="email-logs-type-filter"
            >
              <option value="">All Types</option>
              <option value="signup_confirmation">Signup Confirmation</option>
              <option value="referral_progress">Referral Progress</option>
              <option value="beta_invite">Beta Invite</option>
            </select>
          </div>

          {/* Email Preview Modal */}
          {previewHtml && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setPreviewHtml(null)}>
              <div className="bg-white rounded-xl max-w-2xl w-full max-h-[80vh] overflow-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between p-4 border-b">
                  <h3 className="font-semibold text-gray-900">Email Preview</h3>
                  <button onClick={() => setPreviewHtml(null)} className="text-gray-400 hover:text-gray-600 text-sm">Close</button>
                </div>
                <div className="p-1">
                  <iframe
                    srcDoc={previewHtml}
                    title="Email Preview"
                    className="w-full border-0 rounded"
                    style={{ minHeight: '500px' }}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Logs Table */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="email-logs-table">
                <thead>
                  <tr className="border-b border-gray-100 bg-gray-50/50">
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">To</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Type</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Subject</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Provider</th>
                    <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Status</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Sent</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Preview</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {emailLogs.map((log) => (
                    <tr key={log.id} className="hover:bg-gray-50/50">
                      <td className="px-4 py-3 text-sm text-gray-900">{log.to_email}</td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                          log.email_type === 'signup_confirmation' ? 'bg-blue-100 text-blue-700' :
                          log.email_type === 'referral_progress' ? 'bg-violet-100 text-violet-700' :
                          log.email_type === 'beta_invite' ? 'bg-emerald-100 text-emerald-700' :
                          'bg-gray-100 text-gray-600'
                        }`}>
                          {log.email_type === 'signup_confirmation' ? 'Signup' :
                           log.email_type === 'referral_progress' ? 'Referral' :
                           log.email_type === 'beta_invite' ? 'Beta Invite' : log.email_type}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 max-w-[200px] truncate">{log.subject}</td>
                      <td className="px-4 py-3 text-xs text-gray-400 uppercase">{log.provider}</td>
                      <td className="px-4 py-3 text-center">
                        {log.success ? (
                          <CheckCircle className="w-4 h-4 text-emerald-500 mx-auto" />
                        ) : (
                          <span className="text-xs text-red-500">Failed</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500">{formatDate(log.created_at)}</td>
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={() => handlePreviewEmail(log.id)}
                          className="text-emerald-600 hover:text-emerald-700 text-sm flex items-center gap-1 ml-auto"
                          data-testid={`email-preview-${log.id}`}
                        >
                          <Eye className="w-3 h-3" /> View
                        </button>
                      </td>
                    </tr>
                  ))}
                  {emailLogs.length === 0 && (
                    <tr>
                      <td colSpan={7} className="px-4 py-12 text-center text-gray-400">
                        <Inbox className="w-10 h-10 mx-auto mb-3 opacity-40" />
                        <p>No emails sent yet</p>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            {emailTotalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100">
                <p className="text-sm text-gray-500">Page {emailPage} of {emailTotalPages}</p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setEmailPage(p => Math.max(1, p - 1))}
                    disabled={emailPage <= 1}
                    className="p-2 text-gray-400 hover:text-gray-600 disabled:opacity-30 rounded-lg hover:bg-gray-100"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setEmailPage(p => Math.min(emailTotalPages, p + 1))}
                    disabled={emailPage >= emailTotalPages}
                    className="p-2 text-gray-400 hover:text-gray-600 disabled:opacity-30 rounded-lg hover:bg-gray-100"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default WaitlistPage;
